/**
 * Generates HTML email template for flight status notifications.
 * Uses shared layout from emailLayout.js for header/footer.
 */

import { icon, spacer, emailDocOpen, emailDocClose, emailHeader, emailFooter } from './emailLayout';
import { resolveAircraftImageUrl, getEffectiveAircraft } from '@/features/tails/utils';
import { selectPrimaryBidOption } from '@/features/quotes/utils/selectAcceptedBidOption';

const formatPriceForEmail = (price) => {
  if (price == null || isNaN(price)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);
};

const formatDateForEmail = (dateString) => {
  if (!dateString) return '';
  const d = new Date(dateString);
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
};

const formatTimeForEmail = (dateString) => {
  if (!dateString) return '';
  const d = new Date(dateString);
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
};

/**
 * Render a single aircraft card HTML block for a bid option
 */
const buildAircraftCardHtml = async (option, quote) => {
  const aircraft = getEffectiveAircraft(option?.aircraft, quote);
  const operator = option?.operator;

  const imageUrl = await resolveAircraftImageUrl(aircraft);

  const modelName = (() => {
    if (!aircraft) return '';
    // Per-quote display override wins first — matches getAircraftModelName
    // so the email header shows the broker's edited Aircraft Name rather
    // than the base make_model.
    if (aircraft.display_name_override) return aircraft.display_name_override;
    if (aircraft.aircraft_make_model) {
      const { manufacturer } = aircraft.aircraft_make_model;
      const makeModel = aircraft.make_model || aircraft.aircraft_make_model.make_model || '';
      if (manufacturer && makeModel) {
        const modelPart = makeModel.replace(new RegExp(`^${manufacturer}[- ]*`, 'i'), '');
        return `${manufacturer} ${modelPart}`.trim();
      }
      return makeModel || '';
    }
    return aircraft.make_model || aircraft.tail_number || '';
  })();

  const price = formatPriceForEmail(
    (parseFloat(option?.total_price) || 0) +
    (parseFloat(option?.broker_markup) || 0) +
    (parseFloat(option?.federal_excise_tax) || 0) +
    (parseFloat(option?.platform_fee) || 0) +
    (parseFloat(option?.segment_tax) || 0)
  );

  const badges = [];
  if (operator?.argus_rating && operator.argus_rating !== 'No') {
    badges.push({ label: operator.argus_rating.toUpperCase(), color: '#374151', bg: '#e8e9eb', iconName: 'trophy' });
  }
  if (operator?.wyvern_rating && operator.wyvern_rating !== 'No') {
    badges.push({ label: 'WINGMAN', color: '#374151', bg: '#e8e9eb', iconName: 'medal' });
  }
  if (aircraft?.aircraft_features?.no_aog) {
    badges.push({ label: 'NO AOG', color: '#15803D', bg: '#DCFCE7', iconName: 'check' });
  }

  const badgesHtml = badges.map(b =>
    `<span style="display:inline-block;padding:4px 10px;background:${b.bg};color:${b.color};font-size:11px;font-weight:600;border-radius:16px;margin-right:6px;margin-bottom:4px;">${icon(b.iconName, 14, b.color)} ${b.label}</span>`
  ).join('');

  const amenities = aircraft?.aircraft_features?.custom_features || [];
  const amenitiesHtml = amenities.length > 0
    ? amenities.map(a =>
        `<span style="display:inline-block;padding:4px 12px;background:#e8e9eb;color:#374151;font-size:12px;font-weight:500;border-radius:16px;margin-right:6px;margin-bottom:6px;">${icon('check', 14, '#374151')} ${a}</span>`
      ).join('')
    : '';

  return `
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          ${imageUrl ? `
          <tr>
            <td style="padding:0;line-height:0;font-size:0;">
              <img src="${imageUrl}" alt="${modelName}" width="600" style="display:block;width:100%;height:auto;object-fit:cover;max-height:280px;" />
            </td>
          </tr>` : ''}
          <tr>
            <td style="padding:20px 32px;background-color:#FFFFFF;">
              <h2 style="margin:0 0 4px;font-size:20px;font-weight:500;color:#171717;">${aircraft?.tail_number || ''}</h2>
              <p style="margin:0 0 2px;font-size:13px;color:#6B7280;">${icon('plane', 14, '#6B7280')} ${modelName}</p>

              <!-- Price -->
              <div style="display:inline-block;padding:6px 14px;background-color:#E8F4FD;border-radius:16px;margin-bottom:12px;">
                <span style="font-size:14px;font-weight:700;color:#0C3E71;">${icon('priceTag', 14, '#0C3E71')} ${price}</span>
              </div>

              <!-- Badges -->
              ${badgesHtml ? `<div style="margin-top:8px;">${badgesHtml}</div>` : ''}

              <!-- About -->
              ${aircraft?.description ? `
              <div style="margin-top:16px;">
                <h3 style="margin:0 0 6px;font-size:13px;font-weight:600;color:#6B7280;">About the Aircraft</h3>
                <p style="margin:0;font-size:13px;color:#171717;line-height:1.5;">${aircraft.description}</p>
              </div>` : ''}

              <!-- Aircraft note (per-quote broker note, above Amenities). -->
              ${aircraft?.quote_notes ? `
              <div style="margin-top:16px;padding:12px 14px;background-color:#FFFBEB;border:1px solid #FDE68A;border-radius:8px;">
                <h3 style="margin:0 0 6px;font-size:13px;font-weight:600;color:#92400E;">Aircraft Note</h3>
                <p style="margin:0;font-size:13px;color:#92400E;line-height:1.5;white-space:pre-wrap;">${aircraft.quote_notes}</p>
              </div>` : ''}

              <!-- Amenities -->
              ${amenitiesHtml ? `
              <div style="margin-top:16px;">
                <h3 style="margin:0 0 8px;font-size:13px;font-weight:600;color:#6B7280;">Amenities</h3>
                <div>${amenitiesHtml}</div>
              </div>` : ''}
            </td>
          </tr>
        </table>`;
};

/**
 * Build the HTML email body for flight tracking notification
 * @param {Object} params
 * @param {Object} params.quote - Quote data
 * @param {Object} params.trackingUrl - { flightId, route, url }
 * @param {Array} params.flights - Flight data array
 * @returns {string} HTML email body
 */
export const buildFlightEmailHtml = async ({ quote, trackingUrl, flights, organization }) => {
  // This email fires post-payment (flight tracking / progress monitor), so
  // by the time it's sent the client has already picked and paid for one
  // aircraft option. Collapse to that one option instead of rendering
  // every shortlisted card — showing all options here would confuse the
  // client into thinking they're being asked to pick again.
  // Uses the "primary" fallback so the email still renders SOMETHING if
  // the trigger ever fires pre-payment / on drift (first shortlisted,
  // then first raw option).
  const allBidOptions = quote?.bid_items?.flatMap(bi => bi.bid?.bid_options || []) || [];
  const picked = selectPrimaryBidOption(allBidOptions);
  const optionsToRender = picked ? [picked] : [];

  // buildAircraftCardHtml probes image URLs, so it's async; parallelize
  // even though there's usually just one card to build now.
  const aircraftCardsHtml = (
    await Promise.all(optionsToRender.map((option) => buildAircraftCardHtml(option, quote)))
  ).join(`\n        ${spacer()}\n`);

  // Tracking URL
  const primaryTrackingUrl = trackingUrl.url || '#';

  // Leg details
  const legRows = (flights || []).map((f, i) => `
                <tr>
                  <td style="padding:12px 0px;border-bottom:1px solid #E5E7EB;${i === 0 ? 'border-top:1px solid #E5E7EB;' : ''}">
                    <span style="font-size:14px;font-weight:600;background-color: #e8e9eb; border-radius: 16px; padding:2px 10px;">${icon('flightTakeoff', 14, '#374151')} ${f.departure_airport || '—'}</span>
                    <span style="margin:0 8px;vertical-align:middle;">${icon('arrowRight', 14, '#9CA3AF')}</span>
                    <span style="font-size:14px;font-weight:600;background-color: #e8e9eb; border-radius: 16px; padding:2px 10px;">${icon('flightLand', 14, '#515c6e')} ${f.arrival_airport || '—'}</span>
                  </td>
                  <td style="padding:12px 32px;border-bottom:1px solid #E5E7EB;${i === 0 ? 'border-top:1px solid #E5E7EB;' : ''}text-align:right;">
                    <span style="font-size:13px;color:#6B7280;">${icon('calendar', 13, '#6B7280')} ${formatDateForEmail(f.departure_date || f.departure_time)}</span>
                    <span style="font-size:13px;color:#6B7280;margin-left:12px;">${icon('time', 13, '#6B7280')} ${formatTimeForEmail(f.departure_date || f.departure_time)}</span>
                  </td>
                </tr>`).join('');


  return `
${emailDocOpen({ title: 'Flight Progress Monitor' })}
${emailHeader({ title: 'Flight Progress Monitor', subtitle: `Thank you for using TrustJet.`, organization })}

        ${spacer()}

        <!-- ===== TRACK FLIGHT CTA ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <tr>
            <td align="left" style="padding:28px 32px;">
              <div style="width:64px;height:64px;background-color:#DCFCE7;border-radius:50%;text-align:center;line-height:64px;margin-bottom:16px;">
                ${icon('plane', 28, '#16A34A')}
              </div>
              <p style="margin:0 0 8px;font-size:17px;font-weight:400;color:#5C5C5C;">See flights's route and expected arrival time.</p>
              <br>
              <a href="${primaryTrackingUrl}" style="display:inline-block;padding:12px 32px;background-color:#0C3E71;color:#FFFFFF;text-decoration:none;font-size:14px;font-weight:400;border-radius:24px;">Track my flight</a>
            </td>
          </tr>
        </table>

        ${spacer()}

        <!-- ===== AIRCRAFT CARDS ===== -->
        ${aircraftCardsHtml}

        ${legRows ? `
        ${spacer()}

        <!-- ===== LEG DETAILS ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#F7F7F7;overflow:hidden;">
          <tr>
            <td style="padding:20px 0;">
              <span style="font-size:18px;text-transform:uppercase;color:#5C5C5C;letter-spacing:0.8px;font-weight:600;">Leg Details</span>
            </td>
          </tr>
          <tr>
            <td style="padding:0;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                ${legRows}
              </table>
            </td>
          </tr>
        </table>` : ''}

        ${spacer()}

${emailFooter()}
${emailDocClose()}`;
};
